// const baseUrl = "http://192.168.1.147:8080";
// const baseUrl = "http://production-test.internal.egzotech.com:8080";
// const baseUrl = "http://localhost:8080";
// const baseUrl = "https://product-api.master-swarm.egzotech.com";
const baseUrl = import.meta.env.VITE_PRODUCT_API;

export interface ProductType {
    id: number;
    name: string;
    creator: { email: string };
    createdAt: string;
    brand: string;
    description: string;
    gtin?: string;
    model?: string | null;
    // serialNumberType?: SerialNumberType;
    currentProductTypeRevision?: ProductTypeRevision;
    productTypeRevisions?: ProductTypeRevision[];
    serialNumberType?: { name: string, representsSingleProduct: boolean }
    manufacturingConfigurationsProductTypes?: ManufacturingConfiguration[];
}


export interface ProductTypeRevision {
    id: number;
    version: number;
    name: string;
    modelName: string;
    creator: { email: string };
    createdAt: string;
    changelog: string;
    productType?: ProductType;
    productTypeRevisionLabels?: ProductTypeRevisionLabel[];
    products?: Product[];
    gtin?: string;
}

export interface Product {
    id: number;
    serialNumber: string;
    creator: { email: string };
    createdAt: string;
    description: string;
    productTypeRevision?: ProductTypeRevision;
    manufacturingOrderRevisionProducts?: { product: Product, manufacturingOrderRevision: ManufacturingOrderRevision, archivedRevision: number }[];
}

export interface ManufacturingOrder {
    id: number;
    name: string;
    description: string;
    creator: { email: string };
    createdAt: string;
    manufacturingOrderRevisions?: ManufacturingOrderRevision[];
}

export interface ManufacturingOrderRevision {
    id: number;
    manufacturingOrder: ManufacturingOrder;
    version: number;
    description: string;
    creator: { email: string };
    createdAt: string;
    approvedBy?: { email: string };
    approvedAt?: string;
    manufacturingOrderRevisionProducts?: { product: Product, quantity: number }[];
    otherData?: { [key: string]: any };
}

export interface ProductTypeRevisionLabel {
    label: Label;
    productTypeRevision: ProductTypeRevision;
}

export interface Label {
    id: number;
    name: string;
    path: string;
    metadata?: { metadata: { [key: string]: string } };
    revision: string;
    creator: { email: string };
    createdAt: string;
    productTypeRevisionLabels?: ProductTypeRevisionLabel[];
    type: "normal" | "template" | "group";
    displayName: string;
    description: string;
    parentLabel: Label | null;
    innerLabels?: { label: Label, productType?: ProductType | null, order: number }[] | null
}

export interface ManufacturingConfiguration {
    id: number;
    name: string;
    group?: {
        id: number;
        name: string,
        type?: string;
        preview?: string
    };
    otherData?: { labelingStepsPreview?: string };
    preview?: string | null;
    productType: ProductType;
    metadata?: { [key: string]: string };
}

export interface ManufacturingConfigurationGroup {
    id: number;
    name: string,
    type?: string;
    preview?: string
}

export interface ManufacturingConfigurations {
    [key: string]: ManufacturingConfiguration[] | undefined;
}

export class ApiError extends Error {
    constructor(
        readonly statusCode: number,
        reason?: { error?: string },
        message?: string
    ) {
        super(message ?? reason?.error);
    }
}

export async function login(token: string) {
    const response = await fetch(`${baseUrl}/login`, {
        method: "POST",
        body: JSON.stringify({ token }),
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function logout(token: string) {
    const response = await fetch(`${baseUrl}/logout`, {
        method: "POST",
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getUser() {
    const response = await fetch(`${baseUrl}/user`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getProducts(page: number = 0, count = 100): Promise<Product[]> {
    const params = new URLSearchParams({
        page: page.toString(),
        count: count.toString()
    });

    const response = await fetch(`${baseUrl}/product?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getProductTypes(): Promise<ProductType[]> {
    const response = await fetch(`${baseUrl}/product/type`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function addProductToDatabase(type: string, hint?: string, description?: string): Promise<Product> {
    const response = await fetch(`${baseUrl}/product`, {
        method: "POST",
        body: JSON.stringify({
            productType: type,
            serialNumberHint: hint,
            description
        }),
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}


export async function addRevisionedProductToDatabase(revisionId: number, hint?: string, description?: string): Promise<Product> {
    const response = await fetch(`${baseUrl}/product`, {
        method: "POST",
        body: JSON.stringify({
            productTypeRevisionId: revisionId,
            serialNumberHint: hint,
            description
        }),
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getSerialNumberInfo(serialNumbers: string[]): Promise<Required<Product>[]> {
    const params = new URLSearchParams();
    params.set("serialNumbers", JSON.stringify(serialNumbers));

    const response = await fetch(`${baseUrl}/product?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getProductsWithType(type: string): Promise<Required<Product>[]> {
    const params = new URLSearchParams();
    params.set("type", type);

    const response = await fetch(`${baseUrl}/product?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function createOrder(orderName: string, description: string, products: { serialNumber: string, quantity: number }[], otherData: object | null = null): Promise<ManufacturingOrder> {
    const response = await fetch(`${baseUrl}/manufacturing/order`, {
        method: "POST",
        body: JSON.stringify({ name: orderName, description, products, otherData }),
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function editOrder(orderId: number, description: string, products: { serialNumber: string, quantity: number }[], otherData: object | null = null): Promise<ManufacturingOrder> {
    const response = await fetch(`${baseUrl}/manufacturing/order`, {
        method: "PUT",
        body: JSON.stringify({ id: orderId, description, products, otherData }),
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function createOrderRevision(orderId: number, description: string, products: { serialNumber: string, quantity: number }[], otherData: object | null = null): Promise<ManufacturingOrderRevision> {
    const response = await fetch(`${baseUrl}/manufacturing/order/revision`, {
        method: "POST",
        body: JSON.stringify({ manufacturingOrderId: orderId, description, products, otherData }),
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function approveOrder(orderId: number): Promise<ManufacturingOrderRevision> {
    const response = await fetch(`${baseUrl}/manufacturing/order/revision/approve`, {
        method: "POST",
        body: JSON.stringify({ manufacturingOrderId: orderId }),
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getOrders(): Promise<(ManufacturingOrder & { manufacturingOrderRevisions: ManufacturingOrderRevision[] })[]> {
    const response = await fetch(`${baseUrl}/manufacturing/order?include=revisions`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getOrderInfo(id: number | string): Promise<Required<ManufacturingOrder>> {
    const params = new URLSearchParams();

    if (typeof id === "number") {
        params.set("id", id.toString());
    }
    else {
        params.set("serial", id.toString());
    }

    const response = await fetch(`${baseUrl}/manufacturing/order?${params}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getManufacturingConfigurations(): Promise<ManufacturingConfigurations> {
    const response = await fetch(`${baseUrl}/manufacturing/configuration`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    const output = await response.json() as {
        groups: {
            [key: number]: ManufacturingConfigurationGroup
        };
        configurations: ManufacturingConfigurations;
    };

    Object.values(output.configurations).forEach(v => v?.forEach(t => t.group ? (t.group = output.groups[t.group as unknown as number]) : null));

    return output.configurations;
}

export async function createManufacturingConfiguration(productType: ProductType, name: string, preview: string, groupId: number): Promise<ManufacturingConfiguration> {
    const response = await fetch(`${baseUrl}/manufacturing/configuration`, {
        method: "POST",
        body: JSON.stringify({ productTypeId: productType.id, name, preview, groupId }),
        credentials: "include",
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function deleteManufacturingConfiguration(id: number): Promise<void> {
    const params = new URLSearchParams({
        id: id.toString()
    })

    const response = await fetch(`${baseUrl}/manufacturing/configuration?${params.toString()}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }
}

export async function getManufacturingConfigurationGroups(): Promise<ManufacturingConfigurationGroup[]> {
    const response = await fetch(`${baseUrl}/manufacturing/configuration/group`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json() as ManufacturingConfigurationGroup[];
}


export async function getLabels(): Promise<Label[]> {
    const response = await fetch(`${baseUrl}/label`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getLabel(id: number): Promise<Label> {
    const params = new URLSearchParams({
        id: id.toString()
    })

    const response = await fetch(`${baseUrl}/label?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getChildLabels(parentLabelId: number): Promise<Label[]> {
    const params = new URLSearchParams({
        parentLabelId: parentLabelId.toString()
    })

    const response = await fetch(`${baseUrl}/label?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function createLabel(label: Omit<Label, "id" | "creator" | "createdAt">): Promise<Label> {
    const response = await fetch(`${baseUrl}/label`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
            ...label,
            baseLabel: label.parentLabel?.id
        })
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function editLabel(label: Omit<Label, "creator" | "createdAt">): Promise<Label> {
    const response = await fetch(`${baseUrl}/label`, {
        method: "PUT",
        credentials: "include",
        body: JSON.stringify({
            ...label,
            baseLabel: label.parentLabel?.id
        })
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function deleteLabel(id: number): Promise<void> {
    const params = new URLSearchParams({
        id: id.toString()
    });

    const response = await fetch(`${baseUrl}/label?${params.toString()}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }
}

export async function getTemplateLabels(): Promise<Label[]> {
    const response = await fetch(`${baseUrl}/label/template`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getTemplateLabel(id: number): Promise<Label> {
    const params = new URLSearchParams({
        id: id.toString()
    })

    const response = await fetch(`${baseUrl}/label/template?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function createTemplateLabel(label: Omit<Label, "id" | "creator" | "createdAt">): Promise<Label> {
    const response = await fetch(`${baseUrl}/label/template`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify(label)
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function editTemplateLabel(label: Omit<Label, "creator" | "createdAt">): Promise<Label> {
    const response = await fetch(`${baseUrl}/label/template`, {
        method: "PUT",
        credentials: "include",
        body: JSON.stringify(label)
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function deleteTemplateLabel(id: number): Promise<void> {
    const params = new URLSearchParams({
        id: id.toString()
    });

    const response = await fetch(`${baseUrl}/label/template?${params.toString()}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }
}

export async function getGroupLabels(): Promise<Label[]> {
    const response = await fetch(`${baseUrl}/label/group`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getGroupLabel(id: number): Promise<Label> {
    const params = new URLSearchParams({
        id: id.toString()
    })

    const response = await fetch(`${baseUrl}/label/group?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function createGroupLabel(label: Omit<Label, "id" | "creator" | "createdAt"> & { contents: { labelId: number; productTypeId?: number | null }[] }): Promise<Label> {
    const response = await fetch(`${baseUrl}/label/group`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify(label)
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function editGroupLabel(label: Omit<Label, "creator" | "createdAt"> & { contents?: { labelId: number; productTypeId?: number | null }[] }): Promise<Label> {
    const response = await fetch(`${baseUrl}/label/group`, {
        method: "PUT",
        credentials: "include",
        body: JSON.stringify(label)
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function deleteGroupLabel(id: number): Promise<void> {
    const params = new URLSearchParams({
        id: id.toString()
    });

    const response = await fetch(`${baseUrl}/label/group?${params.toString()}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }
}

export async function getProductTypeRevision(id: number): Promise<ProductTypeRevision> {
    const params = new URLSearchParams({
        id: id.toString()
    })

    const response = await fetch(`${baseUrl}/product/type/revision?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function createProductTypeRevision(productType: ProductType, name: string, modelName: string, gtin: string, changelog: string): Promise<ProductTypeRevision> {
    const response = await fetch(`${baseUrl}/product/type/revision`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ productTypeId: productType.id, name, modelName, gtin, changelog })
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function addLabelToProductTypeRevision(label: Label, productTypeRevision: ProductTypeRevision): Promise<Label> {
    const response = await fetch(`${baseUrl}/product/type/revision/label`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({ labelId: label.id, productTypeRevisionId: productTypeRevision.id })
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function removeLabelFromProductTypeRevision(label: Label, productTypeRevision: ProductTypeRevision): Promise<void> {
    const response = await fetch(`${baseUrl}/product/type/revision/label`, {
        method: "DELETE",
        credentials: "include",
        body: JSON.stringify({ labelId: label.id, productTypeRevisionId: productTypeRevision.id })
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }
}

export async function getProductType(id: number): Promise<ProductType> {
    const params = new URLSearchParams({
        id: id.toString()
    })

    const response = await fetch(`${baseUrl}/product/type?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}


export async function createProductType(productType: Pick<ProductType, 'name' | 'brand' | 'description' | 'model'> & { serialNumberType: string }): Promise<ProductType> {
    const response = await fetch(`${baseUrl}/product/type`, {
        method: "POST",
        body: JSON.stringify(productType),
        credentials: "include",
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function editProductType(productType: Pick<ProductType, 'id' | 'name' | 'brand' | 'description' | 'model'> & { serialNumberType: string }): Promise<ProductType> {
    const response = await fetch(`${baseUrl}/product/type`, {
        method: "PUT",
        body: JSON.stringify(productType),
        credentials: "include",
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function deleteProductType(id: number): Promise<void> {
    const params = new URLSearchParams({
        id: id.toString()
    })

    const response = await fetch(`${baseUrl}/product/type?${params.toString()}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }
}

export async function getProductTypeByName(name: string): Promise<ProductType> {
    const params = new URLSearchParams({
        name
    })

    const response = await fetch(`${baseUrl}/product/type?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function getProductTypeByGtin(gtin: string): Promise<ProductType | null> {
    if (gtin.length === 0) {
        return null;
    }

    const params = new URLSearchParams({
        gtin: gtin.padStart(14, "0")
    })

    const response = await fetch(`${baseUrl}/product/type?${params.toString()}`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}

export async function editProductTypeRevision(productType: ProductType, revision: ProductTypeRevision) {
    const response = await fetch(`${baseUrl}/product/type`, {
        method: "PUT",
        body: JSON.stringify({
            id: productType.id,
            currentProductTypeRevisionId: revision.id
        }),
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}


export async function deleteProductTypeRevision(id: number): Promise<void> {
    const params = new URLSearchParams({
        id: id.toString()
    })

    const response = await fetch(`${baseUrl}/product/type/revision?${params.toString()}`, {
        method: "DELETE",
        credentials: "include",
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }
}


export async function getAPIVersion(): Promise<{
    version: string;
    build: number;
    environment: string;
}> {
    const response = await fetch(`${baseUrl}/version`, {
        credentials: "include"
    });

    if (!response.ok) {
        throw new ApiError(response.status, await response.json());
    }

    return await response.json();
}
